import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSafeFormData, } from '../types/index';
import { useEventDateTime } from '../hooks/useDateTime';
import { useHandleEventCreation } from '../hooks/useHandleEventCreation';
import { useCalendarIntegrationsContext } from '../context/CalendarIntegrationContext';
import { useEventComposerGlobalContext } from './EventComposerGlobalContext';
import { useConfig } from '../context/ConfigContext';
import { useEventApi } from '../hooks/useEvent/useEventApi';
import { useFetchInitialSpaceForComposer } from '../components/SpaceDetails/hooks/useFetchInitialSpaceForComposer';
import { useSpacePrivacySettings } from '../hooks/useSpacePrivacySettings';
var CreateEventContext = createContext({
    initialEvent: undefined,
    handleEventCreation: function () { },
    fetchingSpace: false,
    createEventError: null,
    creatingEvent: false,
    setCreateEventError: function () { },
    shouldShowPrivacyIndicator: false,
    spaceReservationVisibilityEnabled: false,
});
export var CreateEventContextProvider = function (_a) {
    var _b;
    var children = _a.children, onClose = _a.onClose, onSubmit = _a.onSubmit, onEventChange = _a.onEventChange, initialEvent = _a.initialEvent, parentEvent = _a.parentEvent;
    var _c = useEventComposerGlobalContext(), timezone = _c.timezone, requestOnlySpace = _c.requestOnlySpace, onTrackBufferEvent = _c.onTrackBufferEvent, onTrackComposerEvent = _c.onTrackComposerEvent, onError = _c.onError, hasSpacesReservePublicPermissionForCurrentTenant = _c.hasSpacesReservePublicPermissionForCurrentTenant, permissionsLoading = _c.permissionsLoading;
    var _d = useConfig(), tenantId = _d.tenantId, environmentUrls = _d.environmentUrls, userContext = _d.userContext, accessToken = _d.accessToken;
    var apiUrl = environmentUrls.apiUrl, baseApiUrl = environmentUrls.baseApiUrl;
    var currentOrg = userContext.currentOrg;
    var _e = useFormContext(), subscribe = _e.subscribe, setValue = _e.setValue, formState = _e.formState;
    var formData = useSafeFormData();
    var hasSetDefaultPrivacy = useRef(false);
    var _f = useEventApi({
        onError: onError,
        apiUrl: apiUrl,
        baseApiUrl: baseApiUrl,
        currentOrg: currentOrg,
        tenantId: tenantId,
        accessToken: accessToken,
    }), createEvent = _f.createEvent, createEventError = _f.eventError, setCreateEventError = _f.setEventError, creatingEvent = _f.composingEvent;
    var fetchingSpace = useFetchInitialSpaceForComposer({
        mainSpaceId: initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.spaceId,
    }).fetchingSpace;
    var handleDateChange = useEventDateTime({
        timezone: timezone,
    }).handleDateChange;
    var _g = useCalendarIntegrationsContext(), calendarType = _g.calendarType, calendarMailboxAddress = _g.calendarMailboxAddress;
    var handleEventCreation = useHandleEventCreation({
        formData: formData,
        timezone: timezone,
        createEvent: createEvent,
        calendarType: calendarType,
        calendarMailboxAddress: calendarMailboxAddress,
        requestOnlySpace: requestOnlySpace,
        onSubmit: onSubmit,
        onClose: onClose,
        onTrackBufferEvent: onTrackBufferEvent,
        onTrackComposerEvent: onTrackComposerEvent,
    }).handleEventCreation;
    useEffect(function () {
        var _a, _b;
        var explicitIsPrivate = (_a = parentEvent === null || parentEvent === void 0 ? void 0 : parentEvent.isPrivate) !== null && _a !== void 0 ? _a : initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.isPrivate;
        if (explicitIsPrivate !== undefined || hasSetDefaultPrivacy.current) {
            return;
        }
        if (permissionsLoading) {
            return;
        }
        if (formState.dirtyFields.isPrivateEvent) {
            hasSetDefaultPrivacy.current = true;
            return;
        }
        if ((initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.spaceId) && !((_b = formData.spaces) === null || _b === void 0 ? void 0 : _b.length)) {
            return;
        }
        setValue('isPrivateEvent', !hasSpacesReservePublicPermissionForCurrentTenant);
        hasSetDefaultPrivacy.current = true;
    }, [
        permissionsLoading,
        initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.spaceId,
        formData.spaces,
        hasSpacesReservePublicPermissionForCurrentTenant,
        setValue,
        formState.dirtyFields.isPrivateEvent,
        parentEvent === null || parentEvent === void 0 ? void 0 : parentEvent.isPrivate,
        initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.isPrivate,
    ]);
    var _h = useSpacePrivacySettings((_b = formData.spaces) !== null && _b !== void 0 ? _b : []), spaceReservationVisibilityEnabled = _h.spaceReservationVisibilityEnabled, shouldShowPrivacyIndicator = _h.shouldShowPrivacyIndicator;
    useEffect(function () {
        if (!onEventChange)
            return;
        var callback = subscribe({
            formState: {
                values: true,
            },
            callback: function (_a) {
                var values = _a.values;
                onEventChange(values);
            },
        });
        return function () { return callback(); };
    }, [subscribe, onEventChange]);
    /* UseEffect that updates the date and space if the event changes */
    useEffect(function () {
        var _a;
        if (!parentEvent)
            return;
        if (parentEvent.date && !((_a = formData.date) === null || _a === void 0 ? void 0 : _a.isSame(parentEvent.date))) {
            handleDateChange(parentEvent.date);
        }
    }, [formData.date, handleDateChange, parentEvent]);
    return (_jsx(CreateEventContext.Provider, { value: {
            initialEvent: initialEvent,
            fetchingSpace: fetchingSpace,
            handleEventCreation: handleEventCreation,
            createEventError: createEventError,
            creatingEvent: creatingEvent,
            setCreateEventError: setCreateEventError,
            shouldShowPrivacyIndicator: shouldShowPrivacyIndicator,
            spaceReservationVisibilityEnabled: spaceReservationVisibilityEnabled,
        }, children: children }));
};
export var useCreateEventContext = function () {
    return useContext(CreateEventContext);
};
